/**
 * Sentry initialization utility with DSN fallback logic
 * 
 * Priority:
 * 1. VITE_SENTRY_DSN (frontend-specific DSN)
 * 2. Backend DSN (fetched from /api/config/sentry)
 * 3. No Sentry (if neither is available)
 */

import * as Sentry from '@sentry/react';

let sentryInitialized = false;

export async function initializeSentry() {
  if (sentryInitialized) {
    console.log('✓ Sentry already initialized');
    return;
  }

  // ONLY initialize Sentry in production or staging environments
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'production';
  const isProduction = environment === 'production' || environment === 'staging';
  
  if (!isProduction) {
    // console.log(`ℹ Sentry disabled in ${environment} environment`);
    return;
  }

  // Try frontend-specific DSN first
  const frontendDsn = import.meta.env.VITE_SENTRY_DSN;

  if (frontendDsn) {
    console.log('✓ Using frontend Sentry DSN');
    Sentry.init({
      dsn: frontendDsn,
      environment,
      integrations: [
        new Sentry.BrowserTracing({
          // Set tracesSampleRate to 1.0 to capture 100%
          // of transactions for performance monitoring.
          tracePropagationTargets: ['localhost', /^\//],
        }),
        new Sentry.Replay({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0, // Capture 100% of the transactions
      // Session Replay
      replaysSessionSampleRate: 0.1, // Sample 10% of sessions
      replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
    });
    sentryInitialized = true;
    return;
  }

  // Fallback to backend DSN
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
    console.log('⏳ Fetching backend Sentry configuration...');
    
    const response = await fetch(`${apiUrl}/config/sentry`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.warn('⚠ Could not fetch backend Sentry config, Sentry disabled');
      return;
    }

    const config = await response.json();

    if (config.dsn) {
      console.log('✓ Using backend Sentry DSN');
      Sentry.init({
        dsn: config.dsn,
        environment: config.environment || environment,
        integrations: [
          new Sentry.BrowserTracing({
            tracePropagationTargets: ['localhost', /^\//],
          }),
          new Sentry.Replay({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });
      sentryInitialized = true;
    } else {
      console.log('ℹ No Sentry DSN configured');
    }
  } catch (error) {
    console.warn('⚠ Error fetching backend Sentry config:', error.message);
  }
}

/**
 * Manually capture an exception to Sentry
 */
export function captureException(error, context = {}) {
  if (sentryInitialized) {
    Sentry.captureException(error, { extra: context });
  }
}

/**
 * Manually capture a message to Sentry
 */
export function captureMessage(message, level = 'info', context = {}) {
  if (sentryInitialized) {
    Sentry.captureMessage(message, { level, extra: context });
  }
}

/**
 * Set user context for Sentry
 */
export function setUser(user) {
  if (sentryInitialized) {
    Sentry.setUser(user ? {
      id: user.id || user.user_id,
      username: user.username,
      email: user.email,
    } : null);
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message, data = {}) {
  if (sentryInitialized) {
    Sentry.addBreadcrumb({
      message,
      data,
      timestamp: Date.now() / 1000,
    });
  }
}
